export function getStyles(): string {
  return `
    :host {
      display: inline-block;
      --ps-primary: #bb945c;
      --ps-primary-hover: #a07d4e;
      --ps-text: #ffffff;
      --ps-text-secondary: #999999;
      --ps-bg: #111211;
      --ps-bg-secondary: #1a1b1a;
      --ps-border: #333333;
      --ps-radius: 12px;
      --ps-font: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      --ps-overlay: rgba(0, 0, 0, 0.6);
      --ps-error: #ef4444;
      --ps-success: #22c55e;
    }

    * {
      box-sizing: border-box;
      margin: 0;
      padding: 0;
    }

    /* ── Button ─────────────────────────────── */
    .ps-button {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      padding: var(--ps-btn-padding, 12px 24px);
      background: var(--ps-btn-bg, var(--ps-primary));
      color: var(--ps-btn-color, #111211);
      font-family: var(--ps-btn-font, var(--ps-font));
      font-size: var(--ps-btn-font-size, 14px);
      font-weight: var(--ps-btn-font-weight, 600);
      border: var(--ps-btn-border, none);
      border-radius: var(--ps-btn-radius, 8px);
      cursor: pointer;
      transition: all 0.2s ease;
      width: var(--ps-btn-width, auto);
      height: var(--ps-btn-height, auto);
      box-shadow: var(--ps-btn-shadow, none);
      line-height: 1;
      white-space: nowrap;
    }

    .ps-button:hover {
      background: var(--ps-btn-hover-bg, var(--ps-primary-hover));
      color: var(--ps-btn-hover-color, var(--ps-btn-color, #111211));
      transform: translateY(-1px);
    }

    .ps-button:active {
      transform: translateY(0);
    }

    .ps-button svg {
      width: var(--ps-btn-icon-size, 18px);
      height: var(--ps-btn-icon-size, 18px);
      fill: none;
      stroke: var(--ps-btn-icon-color, currentColor);
      stroke-width: 2;
      stroke-linecap: round;
      stroke-linejoin: round;
    }

    /* ── Modal Overlay ──────────────────────── */
    .ps-overlay {
      position: fixed;
      inset: 0;
      background: var(--ps-modal-overlay, var(--ps-overlay));
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 999999;
      opacity: 0;
      visibility: hidden;
      transition: opacity 0.25s ease, visibility 0.25s ease;
      padding: 16px;
    }

    .ps-overlay.ps-open {
      opacity: 1;
      visibility: visible;
    }

    /* ── Modal ──────────────────────────────── */
    .ps-modal {
      background: var(--ps-modal-bg, var(--ps-bg));
      color: var(--ps-modal-color, var(--ps-text));
      border-radius: var(--ps-modal-radius, var(--ps-radius));
      width: var(--ps-modal-width, 100%);
      max-width: var(--ps-modal-max-width, 480px);
      max-height: 90vh;
      overflow-y: auto;
      font-family: var(--ps-modal-font, var(--ps-font));
      position: relative;
      transform: translateY(20px) scale(0.97);
      transition: transform 0.25s ease;
      box-shadow: 0 25px 50px rgba(0, 0, 0, 0.4);
    }

    .ps-open .ps-modal {
      transform: translateY(0) scale(1);
    }

    /* ── Modal Header ───────────────────────── */
    .ps-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 20px 24px;
      background: var(--ps-modal-header-bg, var(--ps-bg-secondary));
      border-bottom: 1px solid var(--ps-border);
      border-radius: var(--ps-modal-radius, var(--ps-radius)) var(--ps-modal-radius, var(--ps-radius)) 0 0;
    }

    .ps-header-title {
      font-size: 16px;
      font-weight: 600;
      color: var(--ps-modal-header-color, var(--ps-text));
    }

    .ps-close {
      width: 32px;
      height: 32px;
      display: flex;
      align-items: center;
      justify-content: center;
      background: none;
      border: none;
      color: var(--ps-modal-close-color, var(--ps-text-secondary));
      cursor: pointer;
      border-radius: 6px;
      transition: background 0.15s;
    }

    .ps-close:hover {
      background: rgba(255, 255, 255, 0.1);
    }

    .ps-close svg {
      width: 20px;
      height: 20px;
      stroke: currentColor;
      stroke-width: 2;
      fill: none;
    }

    /* ── Modal Body ─────────────────────────── */
    .ps-body {
      padding: 24px;
    }

    /* ── Upload Zone ─────────────────────────── */
    .ps-upload-zone {
      border: 2px dashed var(--ps-upload-border, var(--ps-border));
      border-radius: var(--ps-radius);
      padding: 40px 24px;
      text-align: center;
      cursor: pointer;
      transition: all 0.2s ease;
      background: var(--ps-upload-bg, transparent);
    }

    .ps-upload-zone:hover,
    .ps-upload-zone.ps-drag-over {
      border-color: var(--ps-primary);
      background: rgba(187, 148, 92, 0.05);
    }

    .ps-upload-zone input {
      display: none;
    }

    .ps-upload-icon {
      width: 48px;
      height: 48px;
      margin: 0 auto 12px;
      stroke: var(--ps-upload-icon-color, var(--ps-primary));
      fill: none;
      stroke-width: 1.5;
    }

    .ps-upload-text {
      font-size: 14px;
      color: var(--ps-upload-color, var(--ps-text));
      margin-bottom: 4px;
    }

    .ps-upload-hint {
      font-size: 12px;
      color: var(--ps-text-secondary);
    }

    /* ── Preview ────────────────────────────── */
    .ps-preview {
      margin-top: 16px;
      position: relative;
    }

    .ps-preview img {
      width: 100%;
      border-radius: var(--ps-radius);
      display: block;
    }

    .ps-preview-remove {
      position: absolute;
      top: 8px;
      right: 8px;
      width: 28px;
      height: 28px;
      border-radius: 50%;
      background: rgba(0, 0, 0, 0.6);
      border: none;
      color: white;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 16px;
      transition: background 0.15s;
    }

    .ps-preview-remove:hover {
      background: rgba(0, 0, 0, 0.8);
    }

    /* ── Submit Button ──────────────────────── */
    .ps-submit {
      width: 100%;
      padding: 14px;
      margin-top: 20px;
      background: var(--ps-modal-primary-bg, var(--ps-primary));
      color: var(--ps-modal-primary-color, #111211);
      font-family: var(--ps-modal-font, var(--ps-font));
      font-size: 14px;
      font-weight: 600;
      border: none;
      border-radius: var(--ps-modal-primary-radius, 8px);
      cursor: pointer;
      transition: all 0.2s ease;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
    }

    .ps-submit:hover:not(:disabled) {
      opacity: 0.9;
      transform: translateY(-1px);
    }

    .ps-submit:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    /* ── Processing State ───────────────────── */
    .ps-processing {
      text-align: center;
      padding: 40px 24px;
    }

    .ps-spinner {
      width: 48px;
      height: 48px;
      border: 3px solid var(--ps-border);
      border-top-color: var(--ps-loader, var(--ps-primary));
      border-radius: 50%;
      animation: ps-spin 0.8s linear infinite;
      margin: 0 auto 16px;
    }

    @keyframes ps-spin {
      to { transform: rotate(360deg); }
    }

    .ps-processing-text {
      font-size: 14px;
      color: var(--ps-text);
      margin-bottom: 4px;
    }

    .ps-processing-sub {
      font-size: 12px;
      color: var(--ps-text-secondary);
    }

    /* ── Result ─────────────────────────────── */
    .ps-result {
      text-align: center;
    }

    .ps-result img {
      width: 100%;
      border-radius: var(--ps-result-radius, var(--ps-radius));
      display: block;
      margin-bottom: 16px;
    }

    .ps-result-actions {
      display: flex;
      gap: 8px;
    }

    .ps-result-actions button {
      flex: 1;
      padding: 12px;
      font-family: var(--ps-modal-font, var(--ps-font));
      font-size: 13px;
      font-weight: 600;
      border-radius: 8px;
      cursor: pointer;
      transition: all 0.2s;
      border: none;
    }

    .ps-btn-download {
      background: var(--ps-primary);
      color: #111211;
    }

    .ps-btn-download:hover {
      opacity: 0.9;
    }

    .ps-btn-retry {
      background: rgba(255, 255, 255, 0.1);
      color: var(--ps-text);
      border: 1px solid var(--ps-border) !important;
    }

    .ps-btn-retry:hover {
      background: rgba(255, 255, 255, 0.15);
    }

    /* ── Error State ────────────────────────── */
    .ps-error {
      text-align: center;
      padding: 24px;
    }

    .ps-error-icon {
      width: 48px;
      height: 48px;
      margin: 0 auto 12px;
      stroke: var(--ps-error);
      fill: none;
      stroke-width: 1.5;
    }

    .ps-error-text {
      font-size: 14px;
      color: var(--ps-error);
      margin-bottom: 16px;
    }

    /* ── Powered By ─────────────────────────── */
    .ps-powered {
      text-align: center;
      padding: 12px 24px 16px;
      font-size: 11px;
      color: var(--ps-text-secondary);
    }

    .ps-powered a {
      color: var(--ps-primary);
      text-decoration: none;
    }

    .ps-powered a:hover {
      text-decoration: underline;
    }
  `;
}
